import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import cx from 'classnames'

import Badge from 'common/components/Badge'
import Button from 'common/components/button'
import { successToast } from 'common/components/toastNotification'
import { useAppSelector } from 'common/hooks/redux'
import { patchControlLine } from 'features/unifiedFlow/api/api'
import { contentKeys } from 'features/unifiedFlow/api/queryKeys'
import {
  useContent,
  useSelectedComponentElement,
} from 'features/unifiedFlow/contentPage/hooks'

const AiControlLineSection = () => {
  const { data: selectedElement } = useSelectedComponentElement()
  const { content } = useContent()
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const queryClient = useQueryClient()

  const subjectLines = useAppSelector(
    (state) => state.campaignStates.subjectLines
  )
  const controlLines = subjectLines.filter(
    (v) => v.reason_included === 'Control Line'
  )

  const [selectedLine, setSelectedLine] = useState<string>(
    controlLines[0]?.text ?? ''
  )
  const [appliedLine, setAppliedLine] = useState<string | undefined>()
  const [isSaving, setIsSaving] = useState(false)

  if (!controlLines.length) {
    return null
  }

  const onApply = async () => {
    if (!selectedLine || !content || !selectedElement) {
      return
    }

    setIsSaving(true)
    try {
      await patchControlLine({
        accountId,
        contentId: content._id,
        elementId: selectedElement.element_id,
        ownSubjectLine: selectedLine,
        ownSubjectLineAiGenerated: true,
      })
      setAppliedLine(selectedLine)
      queryClient.invalidateQueries({
        queryKey: contentKeys.content(content._id),
      })
      successToast('Control line applied')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <h2 className="font-relais text-2xl font-semibold text-base-700">
          Control line
        </h2>
        <Badge text="Control" variant="control" size="small" />
      </div>
      <p className="text-sm text-base-700">
        Choose an AI generated control line for this workflow:
      </p>
      <div className="flex flex-col gap-3">
        {controlLines.map((line, index) => (
          <button
            key={line._id ?? index}
            type="button"
            onClick={() => setSelectedLine(line.text)}
            className={cx(
              'flex items-center px-4 py-3 rounded-lg border-2 text-left w-full transition-colors cursor-pointer',
              selectedLine === line.text
                ? 'border-gray-700 bg-gray-100'
                : 'border-pink-200 bg-transparent hover:border-pink-400'
            )}
          >
            <span className="mr-3">🥇</span>
            <span className="text-sm text-base-700">{line.text}</span>
          </button>
        ))}
      </div>
      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={!selectedLine || selectedLine === appliedLine || isSaving}
          loading={isSaving}
          onClick={onApply}
        >
          Apply
        </Button>
      </div>
    </div>
  )
}

export default AiControlLineSection
